import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { keepWithinSource } from "./keepWithinSource.js";
import { api, runsOnDevice } from "../api/client.js";
import { providerBody } from "../epg/api.js";
import { isCategoryLocked, useParentalFilter } from "../settings/parentalStore.js";
import { useVodCatalog } from "./vodHooks.js";
import { catalogCategories, queryCatalog } from "./catalogQuery.js";
import { useDiscoveryIndex } from "../discovery/discoveryFiles.js";
import { orderByPopularity } from "../discovery/webDiscovery.js";
import type { CatalogItem, CatalogKind, CatalogPage, CatalogQuery } from "./catalogQuery.js";

/**
 * Screens ask for the slice of the catalog they show (stage B). In the APK the
 * device API answers from native SQLite, so the page never holds the whole
 * catalog; on the website the same query runs over the catalog the server sends
 * (useVodCatalog, already parental-filtered). Parental locks apply either way:
 * on the device the locked categories are excluded in the query itself.
 */

const STALE_MS = 10 * 60 * 1000;

interface Result<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/** Every category of a kind with its count, parental locks NOT applied (see useCatalogGenres). */
function useAllCategories(sourceId: string, kind: CatalogKind, device: boolean): Result<Array<[string, number]>> {
  const query = useQuery({
    queryKey: ["catalogCategories", sourceId, kind],
    queryFn: () => api.post<Array<[string, number]>>(`/api/sources/${sourceId}/vod/categories`, { ...providerBody(), kind }),
    enabled: device && Boolean(sourceId),
    staleTime: STALE_MS,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError, error: query.error };
}

/** The categories the parental settings hide right now, of those given. */
function useLockedCategories(categories: Array<[string, number]> | undefined): string[] {
  const parental = useParentalFilter();
  return useMemo(
    () => (categories ?? []).map(([id]) => id).filter((id) => isCategoryLocked(id, parental)),
    [categories, parental],
  );
}

/** The categories of a kind the parental settings hide right now, on the device (undefined until known). */
export function useLockedCategoryIds(sourceId: string, kind: CatalogKind, enabled = true): string[] | undefined {
  const all = useAllCategories(sourceId, kind, enabled && runsOnDevice());
  const locked = useLockedCategories(all.data);
  return all.data ? locked : undefined;
}

/** A kind's genre (category) ids, sorted, locked ones left out. */
export function useCatalogGenres(sourceId: string, kind: CatalogKind, enabled = true): Result<string[]> {
  const device = runsOnDevice();
  const web = useVodCatalog(sourceId, enabled && !device);
  const all = useAllCategories(sourceId, kind, enabled && device);
  const locked = useLockedCategories(all.data);
  const data = useMemo(() => {
    if (device) {
      if (!all.data) return undefined;
      const hidden = new Set(locked);
      return all.data.map(([id]) => id).filter((id) => !hidden.has(id));
    }
    const items: CatalogItem[] | undefined = kind === "series" ? web.data?.series : web.data?.vod;
    return items ? catalogCategories(items).map(([id]) => id) : undefined;
  }, [device, all.data, locked, web.data, kind]);
  const source = device ? all : web;
  return { data, isLoading: source.isLoading, isError: source.isError, error: source.error };
}

/**
 * Every category of a kind with its count, locked ones INCLUDED. For the
 * parental picker only: it must list the categories it has itself hidden, or a
 * parent could never un-hide one. Never use it for a content surface.
 */
export function useCatalogCategoryCounts(sourceId: string, kind: CatalogKind): Result<Array<[string, number]>> {
  const device = runsOnDevice();
  const web = useVodCatalog(sourceId, !device, true);
  const all = useAllCategories(sourceId, kind, device);
  const local = useMemo(() => {
    if (device) return undefined;
    const items: CatalogItem[] | undefined = kind === "series" ? web.data?.series : web.data?.vod;
    return items ? catalogCategories(items) : undefined;
  }, [device, web.data, kind]);
  if (device) return all;
  return { data: local, isLoading: web.isLoading, isError: web.isError, error: web.error };
}

/** One page of a catalog query. */
export function useCatalogQuery<T extends CatalogItem = CatalogItem>(
  sourceId: string,
  query: CatalogQuery,
  enabled = true,
): Result<CatalogPage<T>> {
  const device = runsOnDevice();
  const web = useVodCatalog(sourceId, enabled && !device);
  const all = useAllCategories(sourceId, query.kind, device && enabled);
  const locked = useLockedCategories(all.data);
  // "Most popular" on the website: the catalog ordered by the discovery files' views (the device
  // does it in SQL). Without the files yet the provider's order stands.
  const popularIndex = useDiscoveryIndex(enabled && !device && query.sort === "popular");
  const remote = useQuery({
    queryKey: ["catalogQuery", sourceId, query, locked],
    queryFn: () =>
      api.post<CatalogPage<T>>(`/api/sources/${sourceId}/vod/query`, {
        ...providerBody(),
        query,
        excludeCategories: locked,
      }),
    enabled: device && enabled && Boolean(sourceId) && all.data !== undefined,
    staleTime: STALE_MS,
    placeholderData: keepWithinSource(sourceId),
  });
  const local = useMemo(() => {
    if (device) return undefined;
    const items = (query.kind === "series" ? web.data?.series : web.data?.vod) as T[] | undefined;
    if (!items) return undefined;
    const ordered = query.sort === "popular" && popularIndex ? orderByPopularity(popularIndex, query.kind, items) : items;
    return queryCatalog(ordered, query);
  }, [device, web.data, query, popularIndex]);
  if (device) {
    return {
      data: remote.data,
      isLoading: all.isLoading || remote.isLoading,
      isError: all.isError || remote.isError,
      error: all.error ?? remote.error,
    };
  }
  return { data: local, isLoading: web.isLoading, isError: web.isError, error: web.error };
}
